import * as Sentry from '@sentry/browser';
import { buildURL, fetchRequest } from 'Utils/apiFetchRequest';
import ElementVisibility from 'Utils/elementVisibility';
import createVideoItem from 'Utils/videoItem';

const __CONTEXT__ = {
    longThumbnail: window.__VUMEDI_HOMEFEED_CONTEXT__.static.longThumbnail,
    siteSlug: window.__VUMEDI_HOMEFEED_CONTEXT__.siteSlug,
};

class LoadingController {
    constructor(parentSelector) {
        this.loadingRoot = document.querySelector(`${parentSelector} .js_loading`);
    }

    stop() {
        this.loadingRoot.remove();
    }

    fail(message) {
        if (this.loadingRoot) {
            this.loadingRoot.innerText = message ?? 'Loading failed.';
            this.loadingRoot.querySelector('.fa')?.remove();
        }
    }
}

class SectionHandler {
    constructor(rootSelector, requestBase, params = {}, videoItemOptionsLong = {}) {
        this.params = params;
        this.requestBase = requestBase;
        this.videoItemOptionsLong = videoItemOptionsLong;

        this.loadingController = new LoadingController(rootSelector);
        this.root = document.querySelector(rootSelector);
    }

    getVideos() {
        return new Promise((resolve, reject) => {
            fetchRequest(buildURL(this.requestBase, this.params))
                .then((data) => {
                    if (!data?.results?.length) {
                        reject(new Error(`${this.requestBase}: empty payload`));
                        return;
                    }

                    let hasCampaign = false;

                    const videos = data.results.map((el, i) => {
                        const target = el.content ? el.content : el;

                        if (el.viewed_time && !target.viewed_time) {
                            target.viewed_time = el.viewed_time;
                        }

                        if (el.campaign_data) {
                            target.campaign_data = el.campaign_data;
                            hasCampaign = true;
                        }

                        if (this.requestBase === '/api/recommended/') {
                            target.urlParams = {
                                source: 'homefeed',
                                idx: i + 1,
                            };
                        }

                        return target;
                    });

                    if (hasCampaign && videos.length > this.params.limit) {
                        videos.pop();
                    }

                    resolve(videos);
                })
                .catch(() => reject(new Error(`${this.requestBase}: API error`)));
        });
    }

    load() {
        this.getVideos()
            .then((videos) => {
                this.loadingController.stop();
                this.render(videos);
                this.handleShowMore();
            })
            .catch((error) => {
                let message;

                if (error.message.includes('empty payload')) {
                    message = 'There are no available videos.';
                } else {
                    Sentry.captureException(error);
                    message = 'Ops, something went wrong.';
                }

                this.loadingController.fail(message);
            });
    }

    render(videos) {
        const list = this.root.querySelector('.homefeed-page__list_items');

        for (const video of videos) {
            const longOptions = {
                thumbnailPlaceholder: __CONTEXT__.longThumbnail,
                includeFeaturing: true,
                includeThumbups: false,
                showAuthor: true,
                showFeatured: !!video.campaign_data,
            };

            const videoItem = createVideoItem('long', video, {
                long: Object.assign(longOptions, this.videoItemOptionsLong),
            });

            if (videoItem) {
                const videoContent = document.createElement('div');
                videoContent.classList.add('homefeed-page__video');
                videoContent.appendChild(videoItem);
                list.appendChild(videoContent);
            }
        }

        this.root.classList.remove('hidden');
    }

    handleShowMore() {
        const showMoreButton = this.root.querySelector('.js_showMore');
        const videoItems = this.root.querySelectorAll('.homefeed-page__list_items .homefeed-page__video');

        let lastVisibleCount = 0;

        const getRowCount = () => {
            const windowWidth = window.innerWidth;

            if (windowWidth >= 1200) return 4;
            if (windowWidth >= 992) return 3;
            if (windowWidth >= 768) return 2;
            return 1;
        };

        const show = (n) => videoItems.forEach((el, i) => el.classList[i < n ? 'remove' : 'add']('hidden'));
        const setHiddenVideos = (videosInRow) => {
            if (lastVisibleCount === videosInRow) return;

            lastVisibleCount = videosInRow;
            show(videosInRow);

            // Reset 'Show more' button if there are hidden videos, but not more than 2 rows
            if (videoItems.length > videosInRow) {
                showMoreButton.classList.remove('vm-button--hidden');
            }
        };

        const handleClick = (ev) => {
            ev.preventDefault();

            // Show 2 rows, even if that means less total videos visible on smaller screens
            show(getRowCount() * 2);
            showMoreButton.classList.add('vm-button--hidden');
        };

        setHiddenVideos(getRowCount());
        window.addEventListener('resize', () => setHiddenVideos(getRowCount()));
        showMoreButton.addEventListener('click', handleClick);
    }
}

class NewsHandler {
    constructor(rootSelector) {
        this.hasNext = true;
        this.infiniteScroll = null;
        this.isLoading = false;
        this.limit = 12;
        this.loadingController = new LoadingController(rootSelector);
        this.offset = 0;
        this.root = document.querySelector(rootSelector);
    }

    setup() {
        const loadingIcon = this.root.querySelector('.js_loading');
        this.infiniteScroll = new ElementVisibility(loadingIcon, () => {
            if (!this.isLoading) {
                this.loadNext();
            }
        });
        this.infiniteScroll.observe();
    }

    getVideos() {
        return new Promise((resolve, reject) => {
            const url = `/api/videos/?site=${__CONTEXT__.siteSlug}&limit=${this.limit}&offset=${this.offset}&exclude_promoted=true&ordering=-created`;

            this.isLoading = true;

            fetchRequest(url)
                .then((data) => {
                    if (!data?.results?.length) {
                        reject(new Error('/api/videos/: empty payload'));
                        return;
                    }

                    this.hasNext = !!data.next;

                    const videos = data.results;

                    if (!videos.length) {
                        reject(new Error('/api/videos/: no video entries'));
                        return;
                    }

                    this.offset += this.limit;

                    // If video from a campaign is returned, total number of response videos is offset + 1
                    if (data.results[0].campaign_data) {
                        videos[0].campaign_data = data.results[0].campaign_data;
                        videos.pop();
                        --this.offset;
                    }

                    resolve(videos);
                })
                .catch(() => reject(new Error('/api/videos/: API error')))
                .finally(() => {
                    this.isLoading = false;
                });
        });
    }

    render(videos) {
        const target = document.querySelector('.js_newsVideos');
        const listTarget = target.querySelector('.homefeed-page__list_items');

        for (const video of videos) {
            const videoItem = createVideoItem('long', video, {
                long: {
                    thumbnailPlaceholder: __CONTEXT__.longThumbnail,
                    includeFeaturing: true,
                    includeThumbups: false,
                    showAuthor: true,
                    showFeatured: !!video.campaign_data,
                },
            });

            if (videoItem) {
                const videoContent = document.createElement('div');
                videoContent.classList.add('homefeed-page__video');
                videoContent.appendChild(videoItem);
                listTarget.appendChild(videoContent);
            }
        }
    }

    loadNext() {
        this.getVideos()
            .then((videos) => {
                this.render(videos);

                if (!this.hasNext) {
                    this.loadingController.stop();
                    this.infiniteScroll.remove();
                }
            })
            .catch((error) => {
                Sentry.captureException(error);
                this.loadingController.fail();
            });
    }
}

function registerEvents() {
    document.querySelector('.js_recommendedVideos .js_showMore')?.addEventListener('click', () => {
        window.eventTracker.trackHomefeedShowMore();
    });
}

function main() {
    const recommended = new SectionHandler('.js_recommendedVideos', '/api/recommended/', {
        limit: 8,
        exclude_promoted: true,
        show_campaigns: true,
        placement: 'heartbeat',
    });
    recommended.load();

    const continueWatching = new SectionHandler(
        '.js_continueWatching',
        '/api/v1/continue-watching/',
        {},
        {
            showViewedProgress: true,
            addURLTimestamp: true,
        },
    );
    continueWatching.load();

    const news = new NewsHandler('.js_newsVideos');
    news.setup();
    news.loadNext();

    registerEvents();
}

window.addEventListener('DOMContentLoaded', main);
